import { useCallback, useEffect } from 'react';

import { usePathBuilder } from '@/hooks/usePathBuilder';
import { UseMultiSearchActions } from '@/types';
import {
  SessionStorageAnalyticProps,
  setSessionData,
  SourceEvent,
  TriggerType,
} from '@/utils/analytics.util';
import {
  BrowserEvent,
  STORAGE_KEYS,
  STORAGE_MESSAGE_TYPES,
} from '@/utils/constants';
import { postMessageToApp, postMessageToWeb } from '@/utils/window.util';

import { useAnalyticsEvents } from './useAnalyticsEvents';

export function useMultiSearchActions({
  lang,
  isNative,
  isInIframe,
  navigateToPLP,
  router,
  setOpenMultiSearch,
  setStoredQueries,
}: UseMultiSearchActions) {
  const { trackListSearchClick } = useAnalyticsEvents();
  const { buildPlpPagePath } = usePathBuilder();

  const handleMultiSearch = useCallback(
    (queries: string[]) => {
      const updatedQuery = normalizedQueries(queries);

      setOpenMultiSearch(false);
      if (updatedQuery.length === 0) return;

      if (updatedQuery.length === 1) {
        const queryValue = updatedQuery[0];
        if (isNative) {
          postMessageToApp({
            type: STORAGE_MESSAGE_TYPES.SET_STORAGE,
            data: { key: STORAGE_KEYS.MULTI_SEARCH_QUERIES, value: queryValue },
          });
        }
        navigateToPLP(queryValue);
        return;
      }
      const firstQuery = updatedQuery[0];
      const multiSearchQueries = updatedQuery.join(',');

      if (isNative) {
        postMessageToApp({
          type: STORAGE_MESSAGE_TYPES.SET_STORAGE,
          data: {
            key: STORAGE_KEYS.MULTI_SEARCH_QUERIES,
            value: multiSearchQueries,
          },
        });
        setSessionData(
          SessionStorageAnalyticProps.MULTI_SEARCH_ANALYTIC_PROPS,
          {
            queries: multiSearchQueries,
          }
        );
        postMessageToApp({
          type: STORAGE_MESSAGE_TYPES.SET_STORAGE,
          data: {
            key: STORAGE_KEYS.IS_MULTI_SEARCH_LIST,
            value: 'true',
          },
        });
      }
      const querySuggestion = {
        sourceEvent: SourceEvent.MANUAL_SEARCH,
        triggerType: TriggerType.ENTER,
      };
      setSessionData(
        SessionStorageAnalyticProps.SEARCH_PERFORM_ANALYTIC_PROPS,
        querySuggestion
      );
      sessionStorage.setItem('selectedMultiSearch', firstQuery);
      sessionStorage.setItem('isMultiSearchList', 'true');
      sessionStorage.setItem('multiSearchQueries', multiSearchQueries);

      const fullPath = buildPlpPagePath({
        isNative,
        query: firstQuery,
        isMultiSearchList: true,
        params: {
          multiSearchQueries,
        },
      });

      if (isInIframe && !isNative) {
        postMessageToWeb(
          {
            type: BrowserEvent.NAVIGATE_TO_PLP,
            query: firstQuery,
            lang,
            isNative,
            path: fullPath,
            isMultiSearchList: 'true',
            multiSearchQueries,
          },
          '*'
        );
        postMessageToWeb(
          {
            type: 'UPDATE_SEARCH_VALUE',
            query: firstQuery,
            isMultiSearchList: 'true',
            multiSearchQueries,
          },
          '*'
        );
      }

      router.push(fullPath);
    },
    [
      buildPlpPagePath,
      lang,
      isNative,
      isInIframe,
      navigateToPLP,
      router,
      setOpenMultiSearch,
    ]
  );

  const handleMultilistSearchClick = useCallback(
    (sourceEvent: SourceEvent.ICON | SourceEvent.MENU = SourceEvent.MENU) => {
      trackListSearchClick({ sourceEvent });
      sessionStorage.removeItem(STORAGE_KEYS.SELECTED_MULTI_SEARCH);
      if (isInIframe && !isNative) {
        postMessageToWeb({ type: BrowserEvent.MULTI_LIST_SEARCH_CLICKED }, '*');
      } else {
        if (isNative) {
          postMessageToApp({
            type: STORAGE_MESSAGE_TYPES.GET_STORAGE,
            data: { key: STORAGE_KEYS.MULTI_SEARCH_QUERIES },
          });
        }
        setOpenMultiSearch(true);
      }
    },
    [isInIframe, isNative, setOpenMultiSearch, trackListSearchClick]
  );

  const handleClearAll = useCallback(() => {
    if (isNative) {
      sessionStorage.removeItem(STORAGE_KEYS.IS_MULTI_SEARCH_LIST);
      sessionStorage.removeItem(STORAGE_KEYS.MULTI_SEARCH_QUERIES);
      postMessageToApp({
        type: STORAGE_MESSAGE_TYPES.REMOVE_STORAGE,
        data: { key: STORAGE_KEYS.MULTI_SEARCH_QUERIES },
      });
    }
    setStoredQueries([]);
  }, [isNative, setStoredQueries]);

  const handleQueriesChange = useCallback((queries: string[]) => {
    const updatedQuery = normalizedQueries(queries);
    if (typeof window === 'undefined' || updatedQuery.length === 0) return;
    sessionStorage.setItem('multiSearchQueries', updatedQuery.join(','));
    if (isNative) {
      postMessageToApp({
        type: STORAGE_MESSAGE_TYPES.SET_STORAGE,
        data: {
          key: STORAGE_KEYS.MULTI_SEARCH_QUERIES,
          value: updatedQuery.join(','),
        },
      });
    }
  }, []);

  return {
    handleMultiSearch,
    handleMultilistSearchClick,
    handleClearAll,
    handleQueriesChange,
  };
}

/**
 * Handles native app (WebView) behavior when the multi-search modal is open:
 * - Polls native storage for multi-search queries when modal opens
 * - Locks body scroll on iOS Safari when modal is open, restores on close
 */
export function useNativeMultiSearchModal(
  isModalOpen: boolean,
  isNative: boolean
): void {
  useEffect(() => {
    if (isModalOpen && isNative) {
      postMessageToApp({
        type: STORAGE_MESSAGE_TYPES.GET_STORAGE,
        data: { key: STORAGE_KEYS.MULTI_SEARCH_QUERIES },
      });
    }
  }, [isModalOpen, isNative]);
}

function normalizedQueries(queries: string[]): string[] {
  return queries.map((query) => query.replace(/\s+/g, ' ').trim());
}

export const handleScrollToTop = () => {
  postMessageToApp({
    type: STORAGE_MESSAGE_TYPES.SCROLL_TO_TOP,
    data: {},
  });
};
